import { v1 as uuidv1 } from 'uuid';
import { ChannelGroupService } from '@/apiServices/channelGroupService';

const CALL_TYPE = 2;
export const moduleState = {
  groups: [],
  isLoading: false,
  // TODO: remove assignment and initialize elsewhere
  isSignup: false,
  channelGroupService: new ChannelGroupService(),
};

export const getters = {
  groups: state => state.groups,
  isLoading: state => state.isLoading,
  isSignup: state => state.isSignup,
  groupById: state => subscriptionGroupId =>
    state.groups.find(g => g.subscriptionGroupId === subscriptionGroupId),

  callChannelsNotInGroup(state, _, rootState) {
    if (!state.groups) {
      return [];
    }

    const subscriptions = state.groups.flatMap(g => g.subscriptions ?? []);
    const subscriptionIds = new Set(subscriptions.map(sub => sub.clientQuoteTypeId));

    if (state.isSignup) {
      // Signup path: use clientQuoteTypes, filter by productTypeId
      const channels = rootState.signup.signupInfo.clientQuoteTypes;
      return channels.filter(
        channel => !subscriptionIds.has(channel.id) && channel.productTypeId === CALL_TYPE
      );
    } else {
      // Normal path: use channels, filter by source
      const channels = rootState.channels.channels;
      return channels.filter(
        channel => !subscriptionIds.has(channel.id) && channel.source === 'Call'
      );
    }
  },

  callChannelEditOptions: (state, moduleGetters, rootState) => editingGroup => {
    const groupId = editingGroup.subscriptionGroupId;
    let channels = [];
    if (state.isSignup) {
      channels = rootState.signup.signupInfo.clientQuoteTypes;
    } else {
      channels = rootState.channels.channels;
    }

    const ungroupedCallChannels = moduleGetters.callChannelsNotInGroup;

    if (!groupId) {
      return ungroupedCallChannels;
    }

    const editingGroupSubIds = editingGroup.subscriptions.map(sub => sub.clientQuoteTypeId);
    const editingChannels = channels.filter(channel => editingGroupSubIds.includes(channel.id));
    return [...ungroupedCallChannels, ...editingChannels];
  },

  isGroupsAvailable(state, _moduleGetters, _rootState, rootGetters) {
    let hasCallChannels = false;
    if (state.isSignup) {
      hasCallChannels = rootGetters['signup/hasCallChannel'];
    } else {
      hasCallChannels = rootGetters['channels/hasCallChannel'];
    }
    return hasCallChannels;
  },

  groupByClientQuoteTypeId: state => clientQuoteTypeId =>
    state.groups.find(g => g.subscriptions.some(s => s.clientQuoteTypeId === clientQuoteTypeId)),
  newGroup(_state, _moduleGetters, _rootState, rootGetters) {
    const id = uuidv1();
    const scheduleInfo = rootGetters['scheduler/scheduleInfo'];
    const group = {
      subscriptionGroupId: id,
      groupName: '',
      subscriptions: [],
      dailySchedule: [],
      monthlyCap: [],
      intervalCapEnabled: false,
      scheduleInfo,
    };

    return group;
  },
  fetchGroup: state => id => state.groups.find(g => g.subscriptionGroupId === id),
};

export const actions = {
  setIsSignup({ commit }) {
    commit('SET_IS_SIGNUP', true);
  },
  resetGroups({ commit }) {
    commit('SET_IS_SIGNUP', false);
    commit('setGroups', []);
  },
  setGroups({ commit }, groups) {
    commit('setGroups', groups);
  },

  async saveGroup({ commit, rootState, state }, { group, channelIds, callback }) {
    commit('setLoading', true);

    // TODO: fix checking for duplicate name without subscriptiongroupid for signup
    const isNameExists = state.groups.some(
      g =>
        g.id !== group.id &&
        g.groupName.localeCompare(group.groupName, undefined, { sensitivity: 'base' }) === 0
    );
    const isChannelGrouped = state.groups.some(
      g =>
        g.subscriptionGroupId !== group.subscriptionGroupId &&
        g.subscriptions.some(s => s.clientQuoteTypeId === group.clientQuoteTypeId)
    );

    if (isNameExists) {
      if (callback) {
        callback(new Error('Name already exists.'));
      }
      return false;
    }

    if (isChannelGrouped) {
      if (callback) {
        callback(new Error('Channel already in group.'));
      }
      return false;
    }

    if (state.isSignup) {
      commit('UPDATE_CHANNEL_GROUP', group);
      commit('signup/SET_CHANNEL_GROUPS', state.groups, { root: true });
    } else {
      const oldGroup = state.groups.find(g => g.subscriptionGroupId === group.subscriptionGroupId);
      const client = rootState.client.client;

      await state.channelGroupService.upsertSubscriptionGroup(client, group, channelIds, oldGroup);
      const groups = await state.channelGroupService.fetchGroups(client.id);
      commit('setGroups', groups);
    }

    commit('setLoading', false);
    return true;
  },

  async deleteSubscriptionGroup({ commit }, groupId) {
    // TODO: move API functions into separate service class.
    commit('setLoading', true);
    commit('deleteSubscriptionGroup', groupId);
    commit('setLoading', false);
  },

  async fetchGroups({ commit, state, rootState }, clientId = rootState.client.client.id) {
    commit('setLoading', true);
    const groups = await state.channelGroupService.fetchGroups(clientId);
    // const groups = results.map(group=> ({ ...group, id: 'subscriptionGroupId' }));

    commit('setGroups', groups);
    commit('setLoading', false);
  },

  async deleteChannelFromGroup({ commit }, groupId, subscriptionId) {
    commit('setLoading', true);
    commit('deleteChannelFromGroup', groupId, subscriptionId);
    commit('setLoading', false);
  },
};

export const mutations = {
  setLoading(state, value) {
    state.isLoading = value;
  },
  setGroups(state, value) {
    state.groups = value;
  },

  SET_IS_SIGNUP(state, isSignup) {
    state.isSignup = isSignup;
  },

  SET_GROUPS(state, apiGroups) {
    const groups = apiGroups.map(group => {
      return { ...group, subscriptionGroupId: group.id };
    });
    state.groups = groups;
  },

  UPDATE_CHANNEL_GROUP(state, channelGroup) {
    const id = channelGroup.subscriptionGroupId;
    const index = state.groups.findIndex(g => g.subscriptionGroupId === id);

    if (index > -1) {
      state.groups.splice(index, 1, channelGroup);
    } else {
      state.groups.push(channelGroup);
    }
  },

  // TODO: make case consistent in mutations.

  deleteSubscriptionGroup(state, id) {
    state.groups = state.groups.filter(g => g.subscriptionGroupId !== id);
  },

  deleteChannelFromGroup(state, { groupId, subscriptionId }) {
    const group = state.groups.find(g => g.id === groupId);
    group.subscriptions = group.subscriptions.filter(s => s.subscriptionId !== subscriptionId);
  },
};
